/**
 * BackupCodesDisplay - Component to display and manage 2FA backup codes
 *
 * Features:
 * - Display backup codes in a grid layout
 * - Copy all codes to clipboard
 * - Download codes as a text file
 * - Visual confirmation of actions
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Copy, Download, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

interface BackupCodesDisplayProps {
  /** Array of backup codes to display */
  codes: string[];
  /** Callback when user confirms they've saved the codes */
  onComplete?: () => void;
  /** Whether to show the completion button */
  showCompleteButton?: boolean;
}

export function BackupCodesDisplay({
  codes,
  onComplete,
  showCompleteButton = true
}: BackupCodesDisplayProps) {
  const [copied, setCopied] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const handleCopyAll = async () => {
    try {
      const codesText = codes.join('\n');
      await navigator.clipboard.writeText(codesText);
      setCopied(true);
      toast.success('Backup codes copied to clipboard');

      // Reset copied state after 3 seconds
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('[BackupCodesDisplay] Failed to copy:', err);
      toast.error('Failed to copy codes');
    }
  };

  const handleDownload = () => {
    try {
      const codesText = [
        'Calie.AI - Two-Factor Authentication Backup Codes',
        '================================================',
        '',
        'Keep these codes in a safe place. Each code can only be used once.',
        '',
        ...codes.map((code, index) => `${index + 1}. ${code}`),
        '',
        `Generated: ${new Date().toISOString()}`,
        '',
        'WARNING: If you lose access to your authenticator app and these codes,',
        'you will not be able to access your account.',
      ].join('\n');

      const blob = new Blob([codesText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'calie-backup-codes.txt';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setDownloaded(true);
      toast.success('Backup codes downloaded');
    } catch (err) {
      console.error('[BackupCodesDisplay] Failed to download:', err);
      toast.error('Failed to download codes');
    }
  };

  const handleComplete = () => {
    if (!copied && !downloaded) {
      toast.warning('Please save your backup codes first');
      return;
    }
    onComplete?.();
  };

  return (
    <div className="space-y-4">
      <Alert variant="destructive" className="bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-800 dark:text-amber-200">
          <strong>Important:</strong> Save these backup codes now. You won't be able to see them again.
          Use these codes to access your account if you lose your authenticator device.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Your Backup Codes</CardTitle>
          <CardDescription>
            Each code can only be used once. Store them securely.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Codes Grid */}
          <div className="grid grid-cols-2 gap-2 p-4 bg-muted rounded-lg font-mono text-sm">
            {codes.map((code, index) => (
              <div
                key={index}
                className="p-2 bg-background rounded border text-center"
              >
                {code}
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              className="flex-1 gap-2"
              onClick={handleCopyAll}
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 text-green-600" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copy All Codes
                </>
              )}
            </Button>
            <Button
              variant="outline"
              className="flex-1 gap-2"
              onClick={handleDownload}
            >
              {downloaded ? (
                <>
                  <Check className="h-4 w-4 text-green-600" />
                  Downloaded!
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Download Codes
                </>
              )}
            </Button>
          </div>

          {/* Completion Status */}
          {(copied || downloaded) && (
            <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
              <Check className="h-4 w-4" />
              {copied && downloaded
                ? 'Codes copied and downloaded'
                : copied
                  ? 'Codes copied to clipboard'
                  : 'Codes downloaded'}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Complete Button */}
      {showCompleteButton && (
        <Button
          onClick={handleComplete}
          className="w-full"
          disabled={!copied && !downloaded}
        >
          I've Saved My Backup Codes
        </Button>
      )}

      {!copied && !downloaded && showCompleteButton && (
        <p className="text-xs text-center text-muted-foreground">
          Please copy or download your backup codes before continuing
        </p>
      )}
    </div>
  );
}
